import { InputNumber, Tag } from 'antd';
import dayjs from 'dayjs';

export const parseCdslCreditDate = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  const ddMmYyyy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (ddMmYyyy) {
    const day = parseInt(ddMmYyyy[1], 10);
    const month = parseInt(ddMmYyyy[2], 10) - 1;
    const year = parseInt(ddMmYyyy[3], 10);
    const parsed = dayjs(new Date(Date.UTC(year, month, day)));
    return parsed.isValid() ? parsed : null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

export const formatReconcileDate = (value) => {
  const parsed = parseCdslCreditDate(value);
  return parsed ? parsed.format('DD MMM YYYY') : '—';
};

export const MATCH_STATE_LABEL = {
  ready: 'Ready to add',
  blocked: 'Blocked',
  unmatched: 'Unmatched',
  ambiguous: 'Ambiguous',
  already_added: 'Already added',
};

export const MATCH_STATE_COLOR = {
  ready: 'green',
  blocked: 'orange',
  unmatched: 'default',
  ambiguous: 'red',
  already_added: 'blue',
};

export const BLOCKER_REASON_LABEL = {
  cdsl_fingerprint_exists: 'Already added via CDSL reconcile',
  cdsl_legacy_fingerprint_exists: 'Already added (legacy fingerprint)',
  manual_lot_exists: 'Already added manually',
  missing_required_fields: 'Missing required fields',
  unknown_isin: 'Unknown ISIN',
  multiple_products_for_isin: 'Multiple products for ISIN',
  invalid_nsdl_demat: 'Invalid NSDL client ID',
  no_supplier_for_demat: 'No supplier for demat',
  multiple_suppliers_for_demat: 'Multiple suppliers for demat',
  multiple_suppliers_for_demat_last8: 'Multiple suppliers for client ID',
};

export function formatBlockerReason(reason) {
  if (!reason) return '—';
  return BLOCKER_REASON_LABEL[reason] || reason;
}

export function rowKeyForMatch(row) {
  return `${row.extraction?.row_index}-${row.product?.uuid || 'none'}-${row.supplier?.uuid || 'none'}-${row.fingerprint || ''}`;
}

export function getDematColumnTitle(statementFormat) {
  return statementFormat === 'nsdl' ? 'Client ID (last 8)' : 'Sender demat';
}

export function getStatementFormatLabel(statementFormat) {
  if (statementFormat === 'nsdl') return 'NSDL';
  return 'CDSL';
}

const txnRefColumn = {
  title: 'Txn ref',
  key: 'transaction_ref',
  render: (_, row) => row.extraction?.transaction_ref || '—',
};

export function getReadyMatchColumns(priceByRowKey, onPriceChange, statementFormat = 'cdsl') {
  return [
    {
      title: 'Product',
      key: 'product',
      render: (_, row) => row.product?.name,
    },
    {
      title: 'Supplier',
      key: 'supplier',
      render: (_, row) => row.supplier?.name,
    },
    { title: 'ISIN', key: 'isin', render: (_, row) => row.extraction?.isin },
    txnRefColumn,
    {
      title: 'Credit date',
      key: 'credit_date',
      render: (_, row) => formatReconcileDate(row.extraction?.date),
    },
    {
      title: 'Qty',
      key: 'qty',
      width: 80,
      render: (_, row) => row.extraction?.qty,
    },
    {
      title: getDematColumnTitle(statementFormat),
      key: 'demat',
      render: (_, row) => row.extraction?.demat_number,
    },
    {
      title: 'Supplier price',
      key: 'supplier_price',
      width: 150,
      render: (_, row) => {
        const key = rowKeyForMatch(row);
        return (
          <InputNumber
            min={0}
            className="w-full"
            placeholder="Price"
            value={priceByRowKey[key]}
            onChange={(v) => onPriceChange(key, v)}
            controls={false}
          />
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, row) => (
        <Tag color={MATCH_STATE_COLOR[row.match_state]}>
          {MATCH_STATE_LABEL[row.match_state]}
        </Tag>
      ),
    },
  ];
}

export function getAlreadyAddedColumns() {
  return [
    {
      title: 'Product',
      key: 'product',
      render: (_, row) => row.product?.name || '—',
    },
    {
      title: 'Supplier',
      key: 'supplier',
      render: (_, row) => row.supplier?.name || '—',
    },
    { title: 'ISIN', key: 'isin', render: (_, row) => row.extraction?.isin },
    txnRefColumn,
    {
      title: 'Credit date',
      key: 'credit_date',
      render: (_, row) => formatReconcileDate(row.extraction?.date),
    },
    { title: 'Qty', key: 'qty', render: (_, row) => row.extraction?.qty },
    {
      title: 'Reason',
      key: 'reason',
      render: (_, row) => formatBlockerReason(row.blocker_reason),
    },
  ];
}

export function getUnmatchedExtractionColumns(statementFormat = 'cdsl') {
  return [
    {
      title: 'Product',
      key: 'product',
      render: (_, row) => row.product?.name || '—',
    },
    { title: 'ISIN', key: 'isin', render: (_, row) => row.extraction?.isin },
    txnRefColumn,
    {
      title: 'Credit date',
      key: 'credit_date',
      render: (_, row) => formatReconcileDate(row.extraction?.date),
    },
    { title: 'Qty', key: 'qty', render: (_, row) => row.extraction?.qty },
    {
      title: getDematColumnTitle(statementFormat),
      key: 'demat',
      render: (_, row) => row.extraction?.demat_number,
    },
    {
      title: 'Why no match',
      key: 'debug',
      render: (_, row) => {
        if (row.blocker_reason) return formatBlockerReason(row.blocker_reason);
        return row.debug_candidates?.[0]?.failure_reasons?.join('; ') || '—';
      },
    },
  ];
}

export function getAmbiguousColumns() {
  return [
    {
      title: 'Product',
      key: 'product',
      render: (_, row) => row.product?.name || '—',
    },
    {
      title: 'Supplier',
      key: 'supplier',
      render: (_, row) => row.supplier?.name || '—',
    },
    { title: 'ISIN', key: 'isin', render: (_, row) => row.extraction?.isin },
    txnRefColumn,
    { title: 'Qty', key: 'qty', render: (_, row) => row.extraction?.qty },
    {
      title: 'Reason',
      key: 'reason',
      render: (_, row) =>
        row.blocker_reason ? formatBlockerReason(row.blocker_reason) : 'Ambiguous match',
    },
  ];
}

export function buildBulkAddPayload(selectedRows, priceByRowKey) {
  return selectedRows.map((row) => {
    const key = rowKeyForMatch(row);
    const parsed = parseCdslCreditDate(row.extraction?.date);
    return {
      fingerprint: row.fingerprint,
      product_id: row.product.uuid,
      supplier_id: row.supplier.uuid,
      supplier_price: Number(priceByRowKey[key]),
      quantity: row.extraction.qty,
      inventory_date: parsed ? parsed.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      remarks: 'CDSL reconciliation',
    };
  });
}

export function selectedRowsMissingPrice(selectedRows, priceByRowKey) {
  return selectedRows.some((row) => {
    const key = rowKeyForMatch(row);
    const price = Number(priceByRowKey[key]);
    return !Number.isFinite(price) || price <= 0;
  });
}
